/**
 * Per-run modifiers ("threats") that reshape the rules of a single run.
 * Picked at the start of each fresh run (post-tutorial) to give every session
 * its own identity and raise the replay ceiling.
 *
 * Modifiers are intentionally simple — one knob on an existing system, no new
 * mechanics — so they're legible at a glance and compose without surprising
 * the player. See game-design-review for the rationale.
 */

export enum ModifierId {
  Volatile = 'volatile',
  Frenzy = 'frenzy',
  PrimeTime = 'prime_time',
  CascadeSurge = 'cascade_surge',
  FreeFirstSplinter = 'free_first_splinter',
}

/** 'challenge' modifiers make the run harder; 'boon' modifiers reward the player. */
export type ModifierTone = 'challenge' | 'boon';

export interface Modifier {
  id: ModifierId;
  name: string;
  description: string;
  tone: ModifierTone;
}

/**
 * Tunable constants for modifier effects. Kept here (not in config.ts) because
 * they're only meaningful in the context of the modifier they back.
 */
export const MODIFIER_VOLATILE_THRESHOLD_DELTA = -3;     // Corruption lands 3 merges sooner
export const MODIFIER_FRENZY_SPAWN_BONUS = 1;            // +1 fragment per post-merge spawn
export const MODIFIER_PRIME_TIME_MULTIPLIER = 2;         // Odd-tier merges score 2x
export const MODIFIER_CASCADE_SURGE_OVERCHARGE_BONUS = 7; // +7 overcharge on each cascade step

export const MODIFIERS_PER_RUN = 2;

export const MODIFIER_POOL: Modifier[] = [
  {
    id: ModifierId.Volatile,
    name: 'Volatile',
    description: 'Elements corrupt 3 merges sooner. Play tight, heal often.',
    tone: 'challenge',
  },
  {
    id: ModifierId.Frenzy,
    name: 'Frenzy',
    description: 'One extra fragment spawns after every merge. The board fills fast.',
    tone: 'challenge',
  },
  {
    id: ModifierId.PrimeTime,
    name: 'Prime Time',
    description: 'Odd-tier merges (Fragment, Crystal, Core, Eternal) score double.',
    tone: 'boon',
  },
  {
    id: ModifierId.CascadeSurge,
    name: 'Cascade Surge',
    description: 'Every cascade step grants +50% Overcharge. Chain for power.',
    tone: 'boon',
  },
  {
    id: ModifierId.FreeFirstSplinter,
    name: 'First Break Free',
    description: 'The first splinter of the run costs no Overcharge.',
    tone: 'boon',
  },
];

/**
 * Pick `count` distinct modifiers at random from the pool.
 * Caller is responsible for deduping against any persistent state.
 */
export function pickRunModifiers(count: number = MODIFIERS_PER_RUN): Modifier[] {
  const pool = [...MODIFIER_POOL];
  const picked: Modifier[] = [];
  const n = Math.min(count, pool.length);
  for (let i = 0; i < n; i++) {
    const index = Math.floor(Math.random() * pool.length);
    picked.push(pool.splice(index, 1)[0]);
  }
  return picked;
}

/**
 * Resolve modifier IDs back to their definitions. Unknown IDs are skipped —
 * safe for loading older saves that may reference retired modifiers.
 */
export function getModifiersByIds(ids: string[]): Modifier[] {
  const byId = new Map(MODIFIER_POOL.map((m) => [m.id, m]));
  const result: Modifier[] = [];
  for (const id of ids) {
    const mod = byId.get(id as ModifierId);
    if (mod) result.push(mod);
  }
  return result;
}

/** Convenience: is a modifier active in this list? */
export function hasModifier(active: Modifier[], id: ModifierId): boolean {
  for (const m of active) if (m.id === id) return true;
  return false;
}
